import type {
  DateChangeReason,
  GridRow,
  ProjectGrid as ProjectGridData,
  ReferenceOption,
} from "@fieldpath/shared";
import { DATE_CHANGE_REASONS, ajeraKey, mdy } from "@fieldpath/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { schedulerUrl } from "../config";
import { navigate } from "../router";
import { PAST_DATE_BOX } from "./DateHint";
import { FieldInput, shownValue } from "./FieldsPanel";
import { KindTag } from "./KindTag";
import { SearchSelect } from "./SearchSelect";
import { useTaskHover, type TaskHoverBind } from "./TaskHoverCard";

/**
 * THE GRID (E2 row 5b): every task of a project in one sweep — owner, due
 * date, the people in each ROLE, hours, and any block still waiting to be
 * linked.
 *
 * **A ROSTER AND AN EDITOR since 13 Oct 2026** (Jeremy, design handoff
 * `docs/design_handoff_project_grid_roster/`, option 1c). The table it
 * replaced was eleven-plus columns of live controls, most role cells hatched
 * "not on this task". Now the left is a short read-only list of every task
 * and the right edits the ONE selected task, showing only what that task has.
 * Clicking a roster row SELECTS it rather than opening the task — the one
 * deliberate exception to "a task row opens the task"; OPEN TASK and the
 * hover card cover navigation. A layout change only: every rule below is the
 * table's, unchanged.
 *
 * **It grants no permission of its own (E2.5).** Each control is editable only
 * where the row's `editability` — the task page's own answer, from the same
 * functions the writes enforce with — says so; elsewhere it shows the value.
 * The edits go through the ordinary routes, so a refusal here is the refusal
 * the task page would give. A Done task is shown, not edited.
 *
 * **Changes are held until Save, and ONE reason covers the sitting (E2.6).**
 * If any date moves later, Save asks for a category once and stamps every
 * date it moves; pulling a date in or setting a first one needs none. Drafts
 * are kept per task, so edits to several tasks, made one after another in the
 * editor, go in one Save.
 *
 * **Roles are matched by name (E2.7).** A role held by more than one person
 * is shown, not edited — one select cannot say "these two" — and the task
 * page is where that is changed.
 *
 * **Field work is in the grid since 28 Oct 2026** (Jeremy; punch list F94-F96).
 * Until then it was left out and counted (E2.8), and a task added from a
 * template had nowhere to get its people. On a task LINKED to a booking, the
 * stage the scheduler owns (`editability.schedulerOwnedStages`) is SHOWN, NOT
 * OFFERED -- its people, date and hours, with the line Plan all stages uses
 * -- and so is a role that holds nothing else ("Field team"). A role holding
 * it beside Fieldpath's own stages is offered, and changes only those
 * (`role-assignment.ts`). An unlinked field task is edited like any other.
 *
 * **Custom fields marked in_grid are in the editor too (E3/4)**, as the server
 * filtered them for this reader. Unlike everything else here they save AS
 * THEY CHANGE, as on every other screen: a field has no date reason to
 * collect and nothing to hold for.
 *
 * **Hours are edited PER STAGE (G.1, Jeremy 12 Oct).** Exact, so no rule for
 * splitting a role's or a task's hours across stages is needed. A completed
 * stage's hours are shown, not edited -- they record what the work was given.
 *
 * **Each stage's DUE DATE sits beside its hours (G.3, Jeremy 13 Oct)**, so a
 * plan is filled in one pass. A stage date is editable where the row's
 * `editability.stageDates` says "direct" -- the task's owner or an admin
 * (E5.2) -- and held for the same Save, under the same one reason when it
 * moves later. A stage's hours and date go in ONE request, so the hours rule
 * (E7.2) and the dates rule (E11) are each checked on the result of both.
 *
 * **A stage that NO ROLE holds is staffed on its own** (Jeremy, 13 Oct):
 * Simple-Assign, Review, QA/QC-PDF's (once "Generic") "PDF / admin finishing" has no role, so assigning by role could
 * never reach it. It gets its own person under Roles, saved in the same
 * request as that stage's hours and date -- the task page's per-stage
 * assignment, not a new right.
 */

interface Draft {
  ownerKey?: string;
  dueDate?: string;
  /** Role name -> the one person chosen ("" for nobody). */
  roles?: Record<string, string>;
  /** Stage id -> hours as typed ("" for not estimated). */
  hours?: Record<string, string>;
  /** Stage id -> due date ("" for none). */
  dates?: Record<string, string>;
  /** Stage id -> the one person on a stage no role holds ("" for nobody). */
  stagePeople?: Record<string, string>;
}

type Row = GridRow;
type Stage = GridRow["stages"][number];
type Role = GridRow["roles"][number];

/** Typed hours as the API takes them: blank is null, anything else a number. */
const hoursValue = (typed: string): number | null => (typed.trim() === "" ? null : Number(typed));

const isDone = (row: Row) => row.status === "Done";

/** The due date as it will be after Save. */
const dueOf = (row: Row, d: Draft | undefined) => d?.dueDate ?? row.dueDate ?? "";

/** Past the client date counting unsaved edits, so the warning answers "if I save this". */
const pastClient = (row: Row, d: Draft | undefined) => {
  const due = dueOf(row, d);
  return row.clientDueDate !== null && due !== "" && due > row.clientDueDate;
};

// The total includes unsaved edits, so it answers "what will this add up to".
const hoursOf = (s: Stage, d: Draft | undefined) => {
  const typed = d?.hours?.[s.id];
  if (typed === undefined) return s.allocatedHours;
  const v = hoursValue(typed);
  return v !== null && Number.isFinite(v) ? v : s.allocatedHours;
};

const totalHours = (row: Row, d: Draft | undefined) =>
  row.stages.reduce<number | null>((sum, s) => {
    const h = hoursOf(s, d);
    return h === null ? sum : (sum ?? 0) + h;
  }, null);

const hoursText = (h: number | null) =>
  h === null ? "—" : `${Number.isInteger(h) ? h : h.toFixed(1)}`;

/**
 * Stages staffed one by one in the editor: no role holds them, so assigning
 * by role cannot reach them. Not an end stage (nobody works there) and not
 * complete (a finished stage keeps whoever did it).
 */
const unroledStages = (row: Row) =>
  row.stages.filter((s) => s.roleId === null && !s.isEnd && s.state !== "complete");

/**
 * The booking's right now: people, date and hours come from the linked
 * resource-scheduler deployment and are changed there. The server's answer
 * (`editability.schedulerOwnedStages`), never worked out here. A completed
 * stage is read-only anyway, and the sync never rewrites one.
 */
const fromBooking = (row: Row, s: Stage) =>
  s.state !== "complete" && row.editability.schedulerOwnedStages.includes(s.id);

/** A role's stages still to come. */
const liveStagesOf = (row: Row, role: Role) =>
  row.stages.filter((s) => s.roleId === role.id && s.state !== "complete");

/**
 * Every stage still to come in this role is the booking's ("Field team" on a
 * linked task), so the role is shown, not offered: the server would refuse
 * any change to it.
 */
const roleFromBooking = (row: Row, role: Role) => {
  const live = liveStagesOf(row, role);
  return live.length > 0 && live.every((s) => fromBooking(row, s));
};

/** A stage's due date as it will be after Save. */
const stageDueOf = (s: Stage, d: Draft | undefined) => d?.dates?.[s.id] ?? s.dueDate ?? "";

/**
 * A date this stage OWES and has not got (E11): on a dates-required workflow,
 * not an end stage, not one that waits on the client or follows such a wait.
 * Shown like an unestimated stage -- a planning gap, flagged, never refused here.
 */
const dateOwed = (row: Row, s: Stage, d: Draft | undefined) =>
  row.requiresDates &&
  !s.isEnd &&
  s.dateRule === null &&
  s.state !== "complete" &&
  stageDueOf(s, d) === "";

/** Hours a stage OWES and has not got. An end stage owes none: nobody works there. */
const hoursOwed = (s: Stage, d: Draft | undefined) =>
  !s.isEnd && s.state !== "complete" && hoursOf(s, d) === null;

/** Nobody in a role, counting unsaved edits. A role held by several is filled. */
const roleOpen = (role: Role, d: Draft | undefined) =>
  role.people.length <= 1 && (d?.roles?.[role.name] ?? role.people[0]?.key ?? "") === "";

/**
 * The roster's diamond: something about this task wants a PM's look. What
 * the booking holds is not counted: nothing here could fill it, and the
 * role's own holders are not who the sync puts on the stage.
 */
const needsAttention = (row: Row, d: Draft | undefined) =>
  !isDone(row) &&
  (pastClient(row, d) ||
    row.roles.some((r) => !roleFromBooking(row, r) && roleOpen(r, d)) ||
    row.stages.some((s) => !fromBooking(row, s) && hoursOwed(s, d)) ||
    row.stages.some((s) => !fromBooking(row, s) && dateOwed(row, s, d)) ||
    row.unresolvedBlock !== null);

/** Whole calendar days from one ISO date to another. */
function daysBetween(from: string, to: string): number {
  const at = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y!, m! - 1, d!);
  };
  return Math.round((at(to) - at(from)) / 86_400_000);
}

/**
 * Grouped under their deliverable, ungrouped work last — the project page's
 * order, so the grid reads as the same project.
 */
function inGroups(rows: readonly Row[]) {
  const groups: { name: string | null; clientDueDate: string | null; rows: Row[] }[] = [];
  for (const row of rows) {
    const g = groups.find((x) => x.name === row.deliverableName);
    if (g) g.rows.push(row);
    else groups.push({ name: row.deliverableName, clientDueDate: row.clientDueDate, rows: [row] });
  }
  groups.sort((a, b) => Number(a.name === null) - Number(b.name === null));
  return groups;
}

export function ProjectGrid({ fetcher, projectId }: { fetcher: Fetcher; projectId: string }) {
  const [grid, setGrid] = useState<ProjectGridData | null>(null);
  const [people, setPeople] = useState<readonly ReferenceOption[]>([]);
  const [managers, setManagers] = useState<readonly ReferenceOption[]>([]);
  const [drafts, setDrafts] = useState<Readonly<Record<string, Draft>>>({});
  const [reason, setReason] = useState<DateChangeReason | "">("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Readonly<Record<string, string>>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rosterRef = useRef<HTMLDivElement>(null);
  const { bind, card } = useTaskHover(fetcher);

  const load = useCallback(async () => {
    try {
      const g = await api.projectGrid(fetcher, projectId);
      setGrid(g);
      // Keep the selection across a reload while the task is still here;
      // otherwise start on the first task that needs a look, or the first.
      const order = inGroups(g.rows).flatMap((x) => x.rows);
      setSelectedId((cur) =>
        cur !== null && order.some((r) => r.taskId === cur)
          ? cur
          : (order.find((r) => needsAttention(r, undefined)) ?? order[0])?.taskId ?? null,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [fetcher, projectId]);

  useEffect(() => {
    void load();
    void api.employees(fetcher).then(setPeople).catch(() => undefined);
    void api.projectManagers(fetcher).then(setManagers).catch(() => undefined);
  }, [fetcher, load]);

  if (error && !grid) return <div className="error">{error}</div>;
  if (!grid) return <p className="muted">Loading the grid…</p>;

  const edit = (taskId: string, change: Draft) =>
    setDrafts((d) => {
      const was = d[taskId] ?? {};
      return {
        ...d,
        [taskId]: {
          ...was,
          ...change,
          roles: { ...was.roles, ...change.roles },
          hours: { ...was.hours, ...change.hours },
          dates: { ...was.dates, ...change.dates },
          stagePeople: { ...was.stagePeople, ...change.stagePeople },
        },
      };
    });

  /** The changes that actually differ from what is stored. */
  const changesFor = (row: GridRow) => {
    const d = drafts[row.taskId];
    if (!d) return null;
    const owner = d.ownerKey !== undefined && d.ownerKey !== (row.ownerKey ?? "") ? d.ownerKey : undefined;
    const due = d.dueDate !== undefined && d.dueDate !== (row.dueDate ?? "") ? d.dueDate : undefined;
    const roles = Object.entries(d.roles ?? {}).filter(([name, key]) => {
      const role = row.roles.find((r) => r.name === name);
      return role && key !== (role.people[0]?.key ?? "");
    });
    const hours = Object.entries(d.hours ?? {}).filter(([stageId, typed]) => {
      const stage = row.stages.find((s) => s.id === stageId);
      return stage && hoursValue(typed) !== stage.allocatedHours;
    });
    const dates = Object.entries(d.dates ?? {}).filter(([stageId, date]) => {
      const stage = row.stages.find((s) => s.id === stageId);
      return stage && date !== (stage.dueDate ?? "");
    });
    const stagePeople = Object.entries(d.stagePeople ?? {}).filter(([stageId, key]) => {
      const stage = row.stages.find((s) => s.id === stageId);
      return stage && key !== (stage.assignees[0]?.key ?? "");
    });
    if (
      owner === undefined &&
      due === undefined &&
      roles.length === 0 &&
      hours.length === 0 &&
      dates.length === 0 &&
      stagePeople.length === 0
    ) {
      return null;
    }
    return { owner, due, roles, hours, dates, stagePeople };
  };

  const changed = grid.rows.filter((r) => changesFor(r) !== null);
  /** A date moved LATER: a reason is owed (E2.6). Setting a first date or pulling one in is not. */
  const later = (from: string | null, to: string) => to !== "" && from !== null && to > from;
  const movesLater = changed.some((r) => {
    const c = changesFor(r)!;
    return (
      (c.due !== undefined && later(r.dueDate, c.due)) ||
      c.dates.some(([stageId, date]) => later(r.stages.find((s) => s.id === stageId)!.dueDate, date))
    );
  });
  const reasonMissing = movesLater && (reason === "" || (reason === "Other" && note.trim() === ""));
  // A typed value that is not a number of hours is caught here, before any
  // request, rather than sent and refused.
  const badHours = changed.some((r) =>
    changesFor(r)!.hours.some(([, typed]) => {
      const v = hoursValue(typed);
      return v !== null && (!Number.isFinite(v) || v < 0);
    }),
  );

  const groups = inGroups(grid.rows);
  const order = groups.flatMap((g) => g.rows);
  const index = Math.max(0, order.findIndex((r) => r.taskId === selectedId));
  const selected = order[index] ?? null;

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    const failed: Record<string, string> = {};
    // One task at a time, each change through its ordinary route. A refusal
    // on one task is reported on that row and does not stop the others.
    for (const row of changed) {
      const c = changesFor(row)!;
      try {
        // The OWNER LAST: handing the task on can take away the saver's own
        // right to set its date, so the date goes while that right holds.
        if (c.due !== undefined) {
          const movedLater = later(row.dueDate, c.due);
          await api.updateTaskDueDate(
            fetcher,
            row.taskId,
            c.due === "" ? null : c.due,
            movedLater && reason !== "" ? reason : undefined,
            movedLater ? note.trim() || null : null,
          );
        }
        // HOURS AND DATES BEFORE PEOPLE: on a workflow that requires them a
        // stage with people must carry hours (E7.3) and a date (E11), so a
        // role put on a stage in the same save only passes in this order.
        // A stage's hours and date go in ONE request, each rule checked on
        // the result of both -- with its person too, when no role holds it.
        for (const stage of row.stages) {
          const typed = c.hours.find(([id]) => id === stage.id)?.[1];
          const date = c.dates.find(([id]) => id === stage.id)?.[1];
          const person = c.stagePeople.find(([id]) => id === stage.id)?.[1];
          if (typed === undefined && date === undefined && person === undefined) continue;
          const movedLater = date !== undefined && later(stage.dueDate, date);
          await api.updateStage(fetcher, row.taskId, stage.id, {
            ...(typed !== undefined ? { allocatedHours: hoursValue(typed) } : {}),
            ...(person !== undefined ? { assigneeKeys: person === "" ? [] : [person] } : {}),
            ...(date !== undefined
              ? {
                  dueDate: date === "" ? null : date,
                  ...(movedLater && reason !== "" ? { reasonCategory: reason } : {}),
                  ...(movedLater ? { reasonNote: note.trim() || null } : {}),
                }
              : {}),
          });
        }
        for (const [name, key] of c.roles) {
          const role = row.roles.find((r) => r.name === name)!;
          await api.setRoleAssignees(fetcher, row.taskId, role.id, key === "" ? [] : [key]);
        }
        if (c.owner !== undefined) {
          await api.updateTask(fetcher, row.taskId, {
            ownerKey: c.owner === "" ? null : ajeraKey(c.owner),
          });
        }
      } catch (e) {
        failed[row.taskId] = e instanceof ApiError ? e.message : String(e);
      }
    }
    setRowErrors(failed);
    // Keep only the drafts that did not save, so a refusal can be corrected.
    setDrafts((d) => Object.fromEntries(Object.entries(d).filter(([id]) => id in failed)));
    if (Object.keys(failed).length === 0) {
      setReason("");
      setNote("");
    }
    // The editor shows one task's error at a time, so open the first refusal
    // in roster order -- otherwise it is a red row the reader has to find.
    const firstFailed = order.find((r) => r.taskId in failed);
    if (firstFailed) setSelectedId(firstFailed.taskId);
    await load();
    setSaving(false);
  };

  const saveField = async (row: GridRow, fieldId: string, value: Parameters<typeof api.setField>[3]) => {
    try {
      await api.setField(fetcher, { level: "task", id: row.taskId }, fieldId, value);
      setRowErrors((e) => {
        const next = { ...e };
        delete next[row.taskId];
        return next;
      });
    } catch (e) {
      setRowErrors((x) => ({ ...x, [row.taskId]: e instanceof ApiError ? e.message : String(e) }));
    }
    await load();
  };

  /** Step through the roster, wrapping at either end — PREV, NEXT, ↑ and ↓. */
  const step = (by: number, focus = false) => {
    if (order.length === 0) return;
    const next = order[(index + by + order.length) % order.length]!;
    setSelectedId(next.taskId);
    if (focus) {
      rosterRef.current
        ?.querySelector<HTMLButtonElement>(`[data-task="${next.taskId}"]`)
        ?.focus();
    }
  };

  const flagged = order.some((r) => needsAttention(r, drafts[r.taskId]));

  return (
    <div className="grid-card">
      {error ? <div className="error grid-card-error">{error}</div> : null}

      {grid.rows.length === 0 ? (
        <div className="grid-empty">
          <p className="muted">No tasks to plan here yet.</p>
        </div>
      ) : (
        <div className="grid-body">
          <div
            className="grid-roster"
            ref={rosterRef}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                step(e.key === "ArrowDown" ? 1 : -1, true);
              }
            }}
          >
            <div className="grid-roster-head" aria-hidden="true">
              <span>Task</span>
              {/* What the rest of Fieldpath calls this person (punch list #45). */}
              <span>PM / TM</span>
              <span>Due</span>
              <span className="num">Hrs</span>
              <span />
            </div>
            {groups.map((g) => (
              <div key={g.name ?? "-"} role="group" aria-label={g.name ?? "Not part of a deliverable"}>
                <div className="grid-group">
                  <span className="grid-group-name">
                    {g.name ? (
                      <>
                        <KindTag kind="deliverable" size="row" /> {g.name}
                      </>
                    ) : (
                      "Not part of a deliverable"
                    )}
                  </span>
                  <span className="grid-group-due">
                    {g.name && g.clientDueDate ? `client due ${mdy(g.clientDueDate)}` : ""}
                  </span>
                </div>
                {g.rows.map((row) => (
                  <RosterRow
                    key={row.taskId}
                    row={row}
                    draft={drafts[row.taskId]}
                    unsaved={changesFor(row) !== null}
                    selected={row.taskId === selected?.taskId}
                    failed={row.taskId in rowErrors}
                    hover={bind}
                    onSelect={() => setSelectedId(row.taskId)}
                  />
                ))}
              </div>
            ))}
            {flagged ? (
              <p className="grid-roster-foot">
                <span className="grid-diamond" aria-hidden="true" /> needs attention
              </p>
            ) : null}
          </div>

          {selected ? (
            <GridEditor
              key={selected.taskId}
              row={selected}
              draft={drafts[selected.taskId] ?? {}}
              position={index + 1}
              of={order.length}
              onStep={step}
              onField={(fieldId, value) => void saveField(selected, fieldId, value)}
              people={people}
              managers={managers}
              error={rowErrors[selected.taskId] ?? null}
              disabled={saving}
              onEdit={(c) => edit(selected.taskId, c)}
            />
          ) : null}
        </div>
      )}

      <div className="grid-savebar">
        {changed.length === 0 ? (
          <span>Changes are held until you save.</span>
        ) : (
          <>
            <span className="grid-savebar-count">
              {changed.length} task{changed.length === 1 ? "" : "s"} changed
            </span>
            {movesLater ? (
              <span className="grid-reason">
                <label htmlFor="grid-why">A date moves later. Reason for this save</label>
                <select
                  id="grid-why"
                  value={reason}
                  onChange={(e) => setReason(e.target.value as DateChangeReason)}
                >
                  <option value="">Choose a reason…</option>
                  {DATE_CHANGE_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <input
                  id="grid-why-note"
                  aria-label={reason === "Other" ? "What is it?" : "Detail (optional)"}
                  placeholder={reason === "Other" ? "What is it?" : "Detail (optional)"}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </span>
            ) : null}
            {badHours ? <span className="error-inline">Hours must be a number, 0 or more.</span> : null}
          </>
        )}
        <button
          className="grid-discard"
          onClick={() => setDrafts({})}
          disabled={saving || changed.length === 0}
        >
          Discard
        </button>
        <button
          className="primary"
          onClick={() => void save()}
          disabled={changed.length === 0 || saving || reasonMissing || badHours}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
      {card}
    </div>
  );
}

/**
 * Where the booking's people, date and hours are changed: Plan all stages'
 * line (StagePlan, 21 Oct 2026), linked to THIS task's deployment as the task
 * page's "Open in scheduler" is -- never to the scheduler's home page, which
 * says nothing about which booking (punch list F95).
 */
function FromBooking({ row }: { row: Row }) {
  return (
    <span className="grid-from-booking">
      from the booking —{" "}
      {row.schedulerMobilizationId ? (
        <a
          href={`${schedulerUrl}/deployments/${row.schedulerMobilizationId}`}
          target="_blank"
          rel="noreferrer"
        >
          change it in the scheduler<span aria-hidden="true"> ↗</span>
        </a>
      ) : (
        "change it in the scheduler"
      )}
    </span>
  );
}

/** One line of the roster. Read-only: clicking SELECTS the task for the editor. */
function RosterRow({
  row,
  draft,
  unsaved,
  selected,
  failed,
  hover,
  onSelect,
}: {
  row: Row;
  draft: Draft | undefined;
  unsaved: boolean;
  selected: boolean;
  failed: boolean;
  hover: TaskHoverBind;
  onSelect: () => void;
}) {
  const done = isDone(row);
  const due = dueOf(row, draft);
  const hours = totalHours(row, draft);
  const cls = ["grid-roster-row", selected && "selected", done && "done", failed && "failed"]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      className={cls}
      data-task={row.taskId}
      aria-pressed={selected}
      onClick={onSelect}
      {...hover(row.taskId, row.title)}
    >
      <span className="grid-roster-title">
        <KindTag kind="task" size="row" />
        <span className="grid-roster-name">{row.title}</span>
        {unsaved ? <span className="grid-unsaved" title="Changes not saved yet" /> : null}
      </span>
      <span className="grid-roster-owner">{row.ownerName ?? "—"}</span>
      <span className={`grid-roster-due${!done && pastClient(row, draft) ? " late" : ""}`}>
        {due === "" ? "—" : mdy(due)}
      </span>
      <span className="grid-roster-hrs">{hoursText(hours)}</span>
      <span
        className={`grid-diamond${needsAttention(row, draft) ? "" : " off"}`}
        aria-label={needsAttention(row, draft) ? "needs attention" : undefined}
      />
    </button>
  );
}

/** The selected task, with only the controls that task has. */
function GridEditor({
  row,
  draft,
  position,
  of,
  onStep,
  onField,
  people,
  managers,
  error,
  disabled,
  onEdit,
}: {
  row: Row;
  draft: Draft;
  position: number;
  of: number;
  onStep: (by: number) => void;
  onField: (fieldId: string, value: Parameters<typeof api.setField>[3]) => void;
  people: readonly ReferenceOption[];
  managers: readonly ReferenceOption[];
  error: string | null;
  disabled: boolean;
  onEdit: (change: Draft) => void;
}) {
  const done = isDone(row);
  const owner = draft.ownerKey ?? row.ownerKey ?? "";
  const due = dueOf(row, draft);
  const late = !done && pastClient(row, draft);
  const hours = totalHours(row, draft);
  // The current owner stays selectable even if no longer flagged a PM:
  // constrain the choice, not the history.
  const ownerOptions =
    row.ownerKey && !managers.some((m) => m.key === row.ownerKey)
      ? [{ key: row.ownerKey, label: row.ownerName ?? row.ownerKey } as ReferenceOption, ...managers]
      : managers;

  return (
    <aside className="grid-editor" aria-label={`Editing ${row.title}`}>
      <div className="grid-stepper">
        <button type="button" onClick={() => onStep(-1)} aria-label="Previous task">
          ‹ Prev
        </button>
        <span>
          {position} of {of}
        </span>
        <button type="button" onClick={() => onStep(1)} aria-label="Next task">
          Next ›
        </button>
      </div>

      {error ? <p className="error-inline grid-editor-error">Not saved: {error}</p> : null}

      <div className="grid-editor-title">
        <h3>
          <KindTag kind="task" size="head" />
          <span>{row.title}</span>
        </h3>
        <p className="grid-editor-sub">
          {row.deliverableName ?? "Not part of a deliverable"}
          {row.workflowName ? ` · ${row.workflowName}` : ""}
        </p>
        <button
          type="button"
          className="grid-open"
          onClick={() => navigate({ kind: "task", id: row.taskId })}
        >
          Open task ›
        </button>
      </div>

      {done ? <p className="grid-done">Done. Shown here, not edited.</p> : null}

      <div className="grid-owner-due">
        <div>
          <label className="grid-label" htmlFor={`grid-owner-${row.taskId}`}>
            PM / TM
          </label>
          {row.editability.handOn && !done ? (
            // Type to find, like every list of people (punch list #27) -
            // this one was missed on 16 Oct.
            <SearchSelect
              id={`grid-owner-${row.taskId}`}
              options={ownerOptions}
              value={owner}
              emptyLabel="Nobody"
              disabled={disabled}
              onChange={(key) => onEdit({ ownerKey: key })}
            />
          ) : (
            <span className="grid-static">{row.ownerName ?? "—"}</span>
          )}
        </div>
        <div>
          <label className="grid-label" htmlFor={`grid-due-${row.taskId}`}>
            Due
          </label>
          {row.editability.dueDate === "direct" && !done ? (
            <>
              <input
                id={`grid-due-${row.taskId}`}
                type="date"
                {...PAST_DATE_BOX}
                className={`grid-due${late ? " late" : ""}`}
                value={due}
                disabled={disabled}
                onChange={(e) => onEdit({ dueDate: e.target.value })}
              />
            </>
          ) : (
            <span className={`grid-static grid-due${late ? " late" : ""}`}>
              {row.dueDate === null ? "—" : mdy(row.dueDate)}
            </span>
          )}
        </div>
      </div>

      {late && row.clientDueDate ? (
        <p className="grid-past-client">
          <span className="grid-diamond" aria-hidden="true" />
          <span>
            Due {mdy(due)}, {daysBetween(row.clientDueDate, due)} day
            {daysBetween(row.clientDueDate, due) === 1 ? "" : "s"} after the client date (
            {mdy(row.clientDueDate)}).
          </span>
        </p>
      ) : null}

      {row.unresolvedBlock ? (
        <div className="grid-waiting">
          <span>Not linked{row.unresolvedBlock.reason ? `: ${row.unresolvedBlock.reason}` : ""}</span>
          <button
            type="button"
            className="linkish"
            onClick={() => navigate({ kind: "task", id: row.taskId })}
          >
            Link it
          </button>
        </div>
      ) : null}

      {row.roles.length > 0 || (!done && unroledStages(row).length > 0) ? (
        <section>
          <h4 className="grid-section">Roles</h4>
          {/* Who is on the work is the accountable people's to change (22 Oct
              2026, `editability.assignOthers`) and GIS staff's on what they hold
              (29 Oct 2026, `roleAssignOthers` / `stageAssignOthers`); everybody
              else reads it. */}
          {done ||
          (row.roles.every((r) => row.editability.roleAssignOthers[r.id]) &&
            unroledStages(row).every((s) => row.editability.stageAssignOthers[s.id])) ? null : (
            <p className="grid-done">
              Only the task's PM / Task Manager, its deliverable's PM, the project's PM or TM, an admin or
              GIS staff someone else put on it changes who holds these.
            </p>
          )}
          {row.roles.map((role) => {
            const several = role.people.length > 1;
            const value = draft.roles?.[role.name] ?? role.people[0]?.key ?? "";
            const open = !done && roleOpen(role, draft);
            if (!done && roleFromBooking(row, role)) {
              // The crew the sync put on the booking's stage, not the role's
              // own holders: the stage is what Workload and the task page read.
              const crew = new Map(
                liveStagesOf(row, role).flatMap((s) => s.assignees.map((p) => [p.key, p.name] as const)),
              );
              return (
                <div key={role.id} className="grid-pair">
                  <span className="grid-pair-label">{role.name}</span>
                  <span className="grid-static grid-booked">
                    {crew.size > 0 ? [...crew.values()].join(", ") : "Nobody"}
                    <FromBooking row={row} />
                  </span>
                </div>
              );
            }
            return (
              <div key={role.id} className="grid-pair">
                <span className="grid-pair-label">{role.name}</span>
                {several ? (
                  <span
                    className="grid-several"
                    title="Several people hold this role; change them on the task page"
                  >
                    {role.people.map((p) => p.name).join(", ")}
                  </span>
                ) : done || !row.editability.roleAssignOthers[role.id] ? (
                  <span className="grid-static">{role.people[0]?.name ?? "Nobody"}</span>
                ) : (
                  // Type to find (punch list #27); × is "Nobody".
                  <SearchSelect
                    ariaLabel={`${role.name} on ${row.title}`}
                    inputClassName={open ? "open" : undefined}
                    options={people}
                    value={value}
                    emptyLabel="Nobody"
                    disabled={disabled}
                    onChange={(key) => onEdit({ roles: { [role.name]: key } })}
                  />
                )}
                {/* A role holding the booking's stage beside its own: a change
                    reaches only its own (role-assignment.ts), so say which
                    stage keeps the crew. No workflow does this on 28 Oct 2026. */}
                {!done && liveStagesOf(row, role).some((s) => fromBooking(row, s)) ? (
                  <span className="grid-from-booking">
                    {liveStagesOf(row, role)
                      .filter((s) => fromBooking(row, s))
                      .map((s) => s.name)
                      .join(", ")}{" "}
                    keeps the booking's crew
                  </span>
                ) : null}
              </div>
            );
          })}
          {done
            ? null
            : unroledStages(row).map((s) => (
                <div key={s.id} className="grid-pair">
                  <span className="grid-pair-label">
                    {s.name}
                    <span
                      className="grid-stage-rule"
                      title="No role holds this stage, so it is staffed on its own"
                    >
                      stage
                    </span>
                  </span>
                  {fromBooking(row, s) ? (
                    <span className="grid-static grid-booked">
                      {s.assignees.map((p) => p.name).join(", ") || "Nobody"}
                      <FromBooking row={row} />
                    </span>
                  ) : s.assignees.length > 1 ? (
                    <span
                      className="grid-several"
                      title="Several people are on this stage; change them on the task page"
                    >
                      {s.assignees.map((p) => p.name).join(", ")}
                    </span>
                  ) : !row.editability.stageAssignOthers[s.id] ? (
                    <span className="grid-static">{s.assignees[0]?.name ?? "Nobody"}</span>
                  ) : (
                    <SearchSelect
                      ariaLabel={`${s.name} on ${row.title}`}
                      options={people}
                      value={draft.stagePeople?.[s.id] ?? s.assignees[0]?.key ?? ""}
                      emptyLabel="Nobody"
                      disabled={disabled}
                      onChange={(key) => onEdit({ stagePeople: { [s.id]: key } })}
                    />
                  )}
                </div>
              ))}
        </section>
      ) : null}

      {row.stages.length > 0 ? (
        <section>
          <h4 className="grid-section">
            <span>Dates and hours by stage</span>
            <span className="grid-section-total">{hoursText(hours)} h</span>
          </h4>
          <div className="grid-stage grid-stage-head" aria-hidden="true">
            <span />
            <span>Due</span>
            <span>Hrs</span>
          </div>
          {row.stages.map((s) => {
            const complete = s.state === "complete" || done;
            const typed = draft.hours?.[s.id] ?? (s.allocatedHours === null ? "" : String(s.allocatedHours));
            const date = stageDueOf(s, draft);
            if (!complete && fromBooking(row, s)) {
              // Shown, not offered: the boxes a finished stage uses, so the
              // values read as fixed, without its grey.
              return (
                <div key={s.id} className="grid-stage">
                  <span className="grid-stage-name">{s.name}</span>
                  <span className="grid-stage-date done">{date === "" ? "—" : mdy(date)}</span>
                  <span className="grid-stage-done" title="Each person on the crew gets these hours">
                    {s.allocatedHours === null ? "—" : `${s.allocatedHours} h`}
                  </span>
                  <FromBooking row={row} />
                </div>
              );
            }
            return (
              <div key={s.id} className={`grid-stage${complete ? " done" : ""}`}>
                <span className="grid-stage-name">
                  {s.name}
                  {s.dateRule ? (
                    <span
                      className="grid-stage-rule"
                      title={
                        s.dateRule === "waiting"
                          ? "Waits on the client or an agency: never needs a date"
                          : "Follows a wait: a date is needed when work moves in"
                      }
                    >
                      {s.dateRule === "waiting" ? "waiting" : "after waiting"}
                    </span>
                  ) : null}
                </span>
                {complete || row.editability.stageDates[s.id] !== "direct" ? (
                  <span
                    className={`grid-stage-date${complete ? " done" : ""}`}
                    title={
                      !complete && row.editability.stageDates[s.id] === "request"
                        ? "Only the task's owner or an admin sets this date; ask for a change on the task page"
                        : undefined
                    }
                  >
                    {date === "" ? "—" : mdy(date)}
                  </span>
                ) : (
                  <input
                    type="date"
                    {...PAST_DATE_BOX}
                    className={`grid-stage-date${dateOwed(row, s, draft) ? " open" : ""}`}
                    aria-label={`Due date for ${s.name} on ${row.title}`}
                    value={date}
                    disabled={disabled}
                    onChange={(e) => onEdit({ dates: { [s.id]: e.target.value } })}
                  />
                )}
                {/* Shown, not offered, to somebody who may not change them:
                    on the stage, the task's PM / Task Manager or an admin
                    (F143, `editability.stageWork`). */}
                {complete || !row.editability.stageWork[s.id] ? (
                  <span
                    className="grid-stage-done"
                    title={
                      complete
                        ? undefined
                        : "Only someone on this stage, the task's PM / Task Manager, its project's PM or TM or an admin changes its hours"
                    }
                  >
                    {s.allocatedHours === null ? "—" : `${s.allocatedHours} h`}
                  </span>
                ) : (
                  // The "h" is drawn beside the number, not typed: the input
                  // stays a plain number, and an unestimated stage shows a
                  // bare dash rather than "— h".
                  <span className={`grid-hours-box${hoursOf(s, draft) === null ? " blank" : ""}`}>
                    <input
                      type="number"
                      min={0}
                      step={0.25}
                      placeholder="—"
                      className={hoursOwed(s, draft) ? "open" : undefined}
                      aria-label={`Hours for ${s.name} on ${row.title}`}
                      value={typed}
                      disabled={disabled}
                      onChange={(e) => onEdit({ hours: { [s.id]: e.target.value } })}
                    />
                  </span>
                )}
              </div>
            );
          })}
          <p className="muted grid-stage-note">
            {row.requiresHours
              ? "Every stage with people on it needs hours; a stage that only waits takes 0."
              : "Blank means nobody has estimated it; 0 means deliberately no time."}
            {row.requiresDates
              ? " It needs a date too, unless it waits on the client."
              : ""}
          </p>
        </section>
      ) : null}

      {row.fields.length > 0 ? (
        <section>
          <h4 className="grid-section">
            <span>Fields</span>
            <span>Save as you type</span>
          </h4>
          <div className="grid-fields">
            {row.fields.map((f) => (
              <div key={f.id} className="grid-pair">
                <span className="grid-pair-label">{f.label}</span>
                {f.mayEdit && !done ? (
                  <FieldInput field={f} compact onSave={(v) => onField(f.id, v)} />
                ) : (
                  <span className={f.value === null ? "muted" : undefined}>{shownValue(f)}</span>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </aside>
  );
}
